import { useState, useCallback } from 'react';

// UX-RT-051 — primitiva condivisa "mutazione → aggiorna subito / rollback su errore".
// Generica, senza accoppiamento al dominio: ogni chiamante fornisce le closure
// sul proprio stato. Riusata in tutto lo sprint (cambio stato, bulk, elimina…).
//
// Uso:
//   const { run, pending } = useOptimisticMutation();
//   await run({
//     optimistic: () => { ...applica il cambio in locale... },  // può ritornare una undo()
//     commit:     () => api.call(),                              // promise verso il server
//     rollback:   () => { ...ripristina lo stato precedente... }, // opzionale se optimistic ritorna undo
//   });
//
// - `optimistic` viene eseguito PRIMA del commit (UI istantanea).
// - se `commit` rigetta: si esegue `rollback` (o la undo ritornata da optimistic)
//   e l'errore viene rilanciato, così il chiamante può mostrare un toast.
// - `pending` è true durante il commit (per disabilitare i controlli).
export default function useOptimisticMutation() {
  const [pending, setPending] = useState(false);

  const run = useCallback(async ({ optimistic, commit, rollback }) => {
    setPending(true);
    const undo = optimistic ? optimistic() : undefined;
    try {
      return await commit();
    } catch (err) {
      const revert = rollback || (typeof undo === 'function' ? undo : undefined);
      if (revert) revert();
      throw err;
    } finally {
      setPending(false);
    }
  }, []);

  return { run, pending };
}
